/**
 * useActiveGym Hook
 *
 * Custom hook that combines the activeGymId from gymStore
 * with gym data fetched via useGym hook.
 *
 * Provides a convenient API for components to:
 * - Check if there's an active gym (hasActiveGym)
 * - Get the active gym data (activeGym)
 * - Check loading/error states
 * - Set or clear the active gym
 */

import { useGymStore } from '../stores/gymStore';
import { useGym } from './useGyms';

/**
 * Hook to manage the active gym state
 *
 * @returns Object containing active gym state and actions
 *
 * @example
 * ```tsx
 * const { activeGym, hasActiveGym, setActiveGym, clearActiveGym } = useActiveGym();
 *
 * if (hasActiveGym) {
 *   console.log(`Currently at: ${activeGym?.name}`);
 * }
 *
 * // Set active gym
 * setActiveGym('gym-123');
 *
 * // Clear active gym
 * clearActiveGym();
 * ```
 */
export function useActiveGym() {
  const activeGymId = useGymStore((state) => state.activeGymId);
  const setActiveGymId = useGymStore((state) => state.setActiveGymId);
  const clearActiveGym = useGymStore((state) => state.clearActiveGym);

  // Convert null to undefined for useGym compatibility
  const { gym, loading, error } = useGym(activeGymId ?? undefined);

  return {
    activeGymId,
    activeGym: gym,
    hasActiveGym: activeGymId !== null,
    loading,
    error,
    setActiveGym: setActiveGymId,
    clearActiveGym,
  };
}
